import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Tabs, Tab, Box, ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Login from './components/Login/Login';
import AuthRoute from './components/Auth/AuthRoute';
import SongInput from './components/Songs/SongInput';
import SongLibrary from './components/Songs/SongLibrary';

const theme = createTheme({
  typography: {
    fontFamily: [
      'Inter',
      'Nunito',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    fontSize: 16,
  },
  palette: {
    background: {
      default: '#1C1C1E', // Deep Charcoal
      paper: '#232326',   // Slightly lighter for cards
    },
    primary: {
      main: '#A259FF', // Electric Purple
      contrastText: '#E5E5E5',
    },
    secondary: {
      main: '#0ACFDC', // Cyan Blue
      contrastText: '#1C1C1E',
    },
    highlight: {
      main: '#FF7A59', // Sunset Orange
      contrastText: '#fff',
    },
    text: {
      primary: '#E5E5E5', // Light Grey
      secondary: '#B3A6D2', // Muted Lavender
    },
    error: {
      main: '#FF7A59', // Use highlight for errors/alerts
    },
    warning: {
      main: '#FF7A59',
    },
    info: {
      main: '#0ACFDC',
    },
    success: {
      main: '#A259FF',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          fontSize: '1.08rem',
          boxShadow: 'none',
          transition: 'background 0.2s, color 0.2s',
        },
        containedPrimary: {
          backgroundColor: '#A259FF',
          color: '#E5E5E5',
          '&:hover': {
            backgroundColor: '#FF7A59',
            color: '#fff',
          },
        },
        containedSecondary: {
          backgroundColor: '#0ACFDC',
          color: '#1C1C1E',
          '&:hover': {
            backgroundColor: '#A259FF',
            color: '#fff',
          },
        },
        containedError: {
          backgroundColor: '#FF7A59',
          color: '#fff',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: '0 4px 24px 0 rgba(162,89,255,0.08)',
          backgroundColor: '#232326',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          background: '#232326',
          color: '#E5E5E5',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#E5E5E5',
        },
        subtitle1: {
          color: '#B3A6D2',
        },
        subtitle2: {
          color: '#B3A6D2',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#B3A6D2',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: '#E5E5E5',
        },
        icon: {
          color: '#B3A6D2',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#E5E5E5',
          '&.Mui-selected': {
            backgroundColor: '#A259FF',
            color: '#fff',
          },
        },
      },
    },
  },
});

function Dashboard() {
  const [currentTab, setCurrentTab] = useState(0);
  const [songToEdit, setSongToEdit] = useState(null);

  useEffect(() => {
    const handleEditSong = (event) => {
      setSongToEdit(event.detail);
      setCurrentTab(0); // Switch to SongInput tab
    };

    window.addEventListener('editSong', handleEditSong);
    return () => window.removeEventListener('editSong', handleEditSong);
  }, []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (newValue === 0) {
      setSongToEdit(null); // Clear edit state when switching to "Add New"
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label={songToEdit ? "Edit Song" : "Add New"} />
          <Tab label="My Songs" />
        </Tabs>

        <Box hidden={currentTab !== 0}>
          <SongInput initialSong={songToEdit} />
        </Box>
        <Box hidden={currentTab !== 1}>
          <SongLibrary />
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route 
            path="/dashboard" 
            element={
              <AuthRoute>
                <Dashboard />
              </AuthRoute>
            } 
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App; 